import {
  CompanyDuplicateListResponseSchema,
  CompanyListResponseSchema,
  CompanySchema,
} from "@l30/shared";
import { keepPreviousData, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch, apiFetchUnchecked } from "./client.js";

import type { Company, CompanyCreate, CompanyUpdate } from "@l30/shared";

const COMPANIES_KEY = ["companies"] as const;

export function useCompanies(
  query?: {
    search?: string;
    limit?: number;
    offset?: number;
    /** Admin-only: include archived companies. Server enforces. */
    includeArchived?: boolean;
    /** Admin-only: show only archived companies. Server enforces. */
    archivedOnly?: boolean;
  },
  options?: { enabled?: boolean },
) {
  const params = new URLSearchParams();
  if (query?.search) params.set("search", query.search);
  if (query?.limit) params.set("limit", String(query.limit));
  if (query?.offset) params.set("offset", String(query.offset));
  if (query?.includeArchived) params.set("includeArchived", "true");
  if (query?.archivedOnly) params.set("archivedOnly", "true");

  const qs = params.toString();
  return useQuery({
    queryKey: [...COMPANIES_KEY, query],
    queryFn: () => apiFetch(`/companies${qs ? `?${qs}` : ""}`, {}, CompanyListResponseSchema),
    enabled: options?.enabled ?? true,
    // Keep the current page visible while the next loads (assessment U8).
    placeholderData: keepPreviousData,
  });
}

export function useCompany(id: string) {
  return useQuery({
    queryKey: [...COMPANIES_KEY, id],
    queryFn: () => apiFetch(`/companies/${id}`, {}, CompanySchema),
    enabled: !!id,
  });
}

export function useCompanyDuplicateCandidates(query?: { limit?: number }) {
  const params = new URLSearchParams();
  if (query?.limit) params.set("limit", String(query.limit));
  const qs = params.toString();
  return useQuery({
    queryKey: [...COMPANIES_KEY, "duplicates", query],
    queryFn: () =>
      apiFetch(
        `/companies/duplicates${qs ? `?${qs}` : ""}`,
        {},
        CompanyDuplicateListResponseSchema,
      ),
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CompanyCreate) =>
      apiFetchUnchecked<Company>("/companies", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: COMPANIES_KEY }),
  });
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: CompanyUpdate & { id: string }) =>
      apiFetchUnchecked<Company>(`/companies/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: COMPANIES_KEY }),
  });
}

/** Hard-delete a company. Admin only. */
export function useDeleteCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetchUnchecked<void>(`/companies/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: COMPANIES_KEY }),
  });
}

export function useArchiveCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<Company>(`/companies/${id}/archive`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: COMPANIES_KEY }),
  });
}

/** Restore an archived company. Admin only. */
export function useUnarchiveCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<Company>(`/companies/${id}/unarchive`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: COMPANIES_KEY }),
  });
}

export function useMergeCompanies() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { sourceId: string; targetId: string }) =>
      apiFetch<Company>(
        "/companies/merge",
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        CompanySchema,
      ),
    onSuccess: (company) => {
      qc.invalidateQueries({ queryKey: COMPANIES_KEY });
      qc.invalidateQueries({ queryKey: [...COMPANIES_KEY, "duplicates"] });
      qc.invalidateQueries({ queryKey: [...COMPANIES_KEY, company.id] });
      qc.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}
